import * as Sentry from '@sentry/nextjs'

import { parseErrorResponseText } from '~/api-client/food_api/error'

/**
 * ギフト購入・送信の調査用ログ。ブラウザだけで動かす。
 *
 * 成功と 4xx は breadcrumb のみ。Issue は 5xx、通信失敗、想定外の例外だけ。
 * その Issue だけを、同じブラウザで 30 秒に 1 件へまとめる。
 * トークン全文・レシート・メールは載せない。
 * ユーザー識別はセッションの userId を Sentry の user.id に入れる。
 */

const FLOW = 'gift'
const GIFT_ISSUE_EXTRA = 'gift_issue'
const EVENT_DEDUPE_MS = 30_000
const BREADCRUMB_DEDUPE_MS = 60_000
const TRACE_TTL_MS = 10 * 60_000

const GIFT_ENDPOINTS = new Set([
  '/static/gift_categories',
  '/static/gift',
  '/static/sale_gifts',
  '/static/sale_gift',
  '/user/owned_gifts',
  '/user/gifted_users',
  '/user/dinner_gift_suggestion',
  '/user/read_dinner_gift_suggestion',
  '/user/in_app_purchased_products',
  '/user/new_in_app_purchased_product',
  '/user/exchange_amazon_gift',
  '/user/new_owned_point',
  '/user/auto_charge_suggestion',
  '/chat/new_chat_message_gift',
  '/chat/new_chat_message_iap_gift',
  '/tweet/new_tweet_gift',
  '/tweet/new_tweet_iap_gift',
  '/tweet/new_reply_gift',
  '/tweet/new_reply_iap_gift',
])

const QUERY_KEYS = new Set(['gift_id', 'user_id', 'required_point_amount'])

type GiftValue = string | number | boolean | null
type GiftData = Record<string, GiftValue>

export type GiftTrace = {
  gift_id?: string
  product_id?: string
  target?: 'chat' | 'tweet' | 'reply' | 'point'
  chat_space_id?: string
  tweet_id?: string
  tweet_reply_id?: string
  to_user_id?: string
  payment_type?: string
}

type BridgeRequest = {
  handler: string
  target: 'chat' | 'tweet' | 'reply'
  chat_space_id?: string
  to_user_id?: string
  tweet_id?: string
  tweet_reply_id?: string
}

const recentEvents = new Map<string, number>()
const recentBreadcrumbs = new Map<string, number>()

let giftTrace: { at: number; fields: GiftTrace } | undefined
let bridgeRequest: { at: number; fields: BridgeRequest } | undefined

type BeforeSend = NonNullable<
  NonNullable<Parameters<typeof Sentry.init>[0]>['beforeSend']
>

const isClient = () => typeof window !== 'undefined'

export const isInAppWebView = (): boolean => {
  try {
    return isClient() && window.flutter_inappwebview != null
  } catch (error) {
    console.error(error)
    return false
  }
}

export const setSentryUser = (userId: string | null | undefined) => {
  try {
    if (userId) {
      Sentry.setUser({ id: userId })
    } else {
      Sentry.setUser(null)
    }
  } catch (error) {
    console.error(error)
  }
}

export const setSentryInApp = (inApp: boolean) => {
  try {
    Sentry.setTag('in_app', inApp ? 'true' : 'false')
  } catch (error) {
    console.error(error)
  }
}

export const setSentryPaymentType = (paymentType: string) => {
  try {
    Sentry.setTag('payment_type', paymentType)
  } catch (error) {
    console.error(error)
  }
}

const asId = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  if (trimmed.length === 0 || trimmed.length > 80) return undefined
  if (/bearer|token|receipt|password|secret/i.test(trimmed)) return undefined
  return trimmed
}

export const summarizeGiftMessage = (message: string): string => {
  try {
    return message
      .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[email]')
      .replace(/bearer\s+[a-z0-9._~+/-]+=*/gi, 'Bearer [redacted]')
      .replace(/[A-Za-z0-9+/_=-]{40,}/g, '[redacted]')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 180)
  } catch (error) {
    console.error(error)
    return ''
  }
}

export const isAlreadyOwnedMessage = (message: string): boolean => {
  try {
    return /すでに所有|既に所有|already\s*owned|item_already_owned|already_owned|itemAlreadyOwned/i.test(
      message
    )
  } catch (error) {
    console.error(error)
    return false
  }
}

const compact = (data: Record<string, GiftValue | undefined>): GiftData => {
  const out: GiftData = {}
  for (const key of Object.keys(data).sort()) {
    const value = data[key]
    if (value === undefined) continue
    out[key] = typeof value === 'string' ? summarizeGiftMessage(value) : value
  }
  return out
}

const breadcrumbKey = (step: string, data: GiftData): string => {
  return `${step}:${JSON.stringify(data)}`
}

const remember = (map: Map<string, number>, key: string, ttl: number) => {
  const now = Date.now()
  if (map.size > 200) {
    map.forEach((at, existing) => {
      if (now - at > ttl) map.delete(existing)
    })
  }
  const prev = map.get(key)
  if (prev != null && now - prev < ttl) return false
  map.set(key, now)
  return true
}

export const giftBreadcrumb = (
  step: string,
  data?: Record<string, GiftValue | undefined>
) => {
  try {
    if (!isClient()) return
    const payload = compact({
      in_app: isInAppWebView(),
      ...data,
    })
    if (
      !remember(
        recentBreadcrumbs,
        breadcrumbKey(step, payload),
        BREADCRUMB_DEDUPE_MS
      )
    ) {
      return
    }
    Sentry.addBreadcrumb({
      category: 'gift',
      type: 'info',
      level: 'info',
      message: step,
      data: payload,
    })
  } catch (error) {
    console.error(error)
  }
}

export const noteGiftTrace = (fields: GiftTrace) => {
  try {
    if (!isClient()) return
    const next: GiftTrace = {}
    const giftId = asId(fields.gift_id)
    const productId = asId(fields.product_id)
    const chatSpaceId = asId(fields.chat_space_id)
    const tweetId = asId(fields.tweet_id)
    const tweetReplyId = asId(fields.tweet_reply_id)
    const toUserId = asId(fields.to_user_id)
    if (giftId) next.gift_id = giftId
    if (productId) next.product_id = productId
    if (chatSpaceId) next.chat_space_id = chatSpaceId
    if (tweetId) next.tweet_id = tweetId
    if (tweetReplyId) next.tweet_reply_id = tweetReplyId
    if (toUserId) next.to_user_id = toUserId
    if (fields.target) next.target = fields.target
    if (fields.payment_type) next.payment_type = fields.payment_type
    giftTrace = { at: Date.now(), fields: { ...freshTrace(), ...next } }
  } catch (error) {
    console.error(error)
  }
}

const freshTrace = (): GiftTrace | undefined => {
  if (giftTrace == null) return undefined
  if (Date.now() - giftTrace.at > TRACE_TTL_MS) return undefined
  return giftTrace.fields
}

export const rememberBridgeRequest = (fields: BridgeRequest) => {
  try {
    if (!isClient()) return
    bridgeRequest = { at: Date.now(), fields }
    noteGiftTrace({
      target: fields.target,
      chat_space_id: fields.chat_space_id,
      to_user_id: fields.to_user_id,
      tweet_id: fields.tweet_id,
      tweet_reply_id: fields.tweet_reply_id,
    })
  } catch (error) {
    console.error(error)
  }
}

const freshBridgeRequest = (): BridgeRequest | undefined => {
  if (bridgeRequest == null) return undefined
  if (Date.now() - bridgeRequest.at > TRACE_TTL_MS) return undefined
  return bridgeRequest.fields
}

const scrubbedError = (error: unknown, fallback: string): Error => {
  const message =
    error instanceof Error
      ? summarizeGiftMessage(error.message)
      : typeof error === 'string'
      ? summarizeGiftMessage(error)
      : fallback
  const scrubbed = new Error(message || fallback)
  scrubbed.name = error instanceof Error ? error.name : 'GiftError'
  if (
    error instanceof Error &&
    error.stack &&
    !/[A-Za-z0-9+/_=-]{40,}/.test(error.stack)
  ) {
    scrubbed.stack = error.stack
  }
  return scrubbed
}

const captureGiftIssue = (args: {
  message: string
  fingerprint: string[]
  tags: Record<string, string | undefined>
  context: Record<string, GiftValue | undefined>
  error?: unknown
}) => {
  if (!isClient()) return
  const tags: Record<string, string> = {
    flow: FLOW,
    in_app: isInAppWebView() ? 'true' : 'false',
  }
  for (const [key, value] of Object.entries(args.tags)) {
    if (value) tags[key] = value
  }
  const context = compact(args.context)

  Sentry.withScope((scope) => {
    for (const [key, value] of Object.entries(tags)) {
      scope.setTag(key, value)
    }
    scope.setContext('gift', context)
    scope.setFingerprint(args.fingerprint)
    scope.setExtra(GIFT_ISSUE_EXTRA, { fingerprint: args.fingerprint })
    scope.setLevel('error')
    if (args.error instanceof Error) {
      Sentry.captureException(scrubbedError(args.error, args.message))
    } else {
      Sentry.captureMessage(args.message, 'error')
    }
  })
}

const endpointFromUrl = (
  url: string
): { pathname: string; query: URLSearchParams } | undefined => {
  try {
    const parsed = new URL(url, 'http://localhost')
    return { pathname: parsed.pathname, query: parsed.searchParams }
  } catch {
    return undefined
  }
}

const queryContext = (
  query: URLSearchParams,
  includeUserId: boolean
): GiftData => {
  const out: GiftData = {}
  QUERY_KEYS.forEach((key) => {
    if (!includeUserId && key === 'user_id') return
    const value = query.get(key)
    if (value == null) return
    const id =
      key === 'required_point_amount' ? value.slice(0, 20) : asId(value)
    if (id) out[key] = id
  })
  return out
}

const responseContext = (data: unknown): GiftData => {
  if (data == null || typeof data !== 'object') return {}
  const body = data as Record<string, unknown>
  const out: GiftData = {}

  if (Array.isArray(body.ownedGifts)) {
    out.count = body.ownedGifts.length
    const total = ownedTotal(body.ownedGifts)
    if (total != null) out.owned_total = total
  } else if (Array.isArray(body.saleGifts)) {
    out.count = body.saleGifts.length
  } else if (Array.isArray(body.categories)) {
    out.count = body.categories.length
  } else if (Array.isArray(body.giftedUsers)) {
    out.count = body.giftedUsers.length
  } else if (Array.isArray(body.purchasedProducts)) {
    out.count = body.purchasedProducts.length
  } else if (Array.isArray(body.salePoints)) {
    out.count = body.salePoints.length
  }

  if (body.gift && typeof body.gift === 'object') {
    const giftId = asId((body.gift as { giftId?: unknown }).giftId)
    if (giftId) out.gift_id = giftId
  }

  const purchased = body.purchasedProduct
  if (purchased && typeof purchased === 'object') {
    const product = purchased as {
      inAppPurchasedProductId?: unknown
      state?: unknown
      gift?: { giftId?: unknown }
    }
    const purchasedId = asId(product.inAppPurchasedProductId)
    const state = asId(product.state)
    const giftId = asId(product.gift?.giftId)
    if (purchasedId) out.purchased_product_id = purchasedId
    if (state) out.purchase_state = state
    if (giftId) out.gift_id = giftId
  }

  if (body.suggestion && typeof body.suggestion === 'object') {
    const pointAmount = (body.suggestion as { pointAmount?: unknown })
      .pointAmount
    if (typeof pointAmount === 'number')
      out.suggested_point_amount = pointAmount
  }

  if (typeof body.availableAmount === 'number') {
    out.available_amount = body.availableAmount
  }

  return out
}

const ownedTotal = (items: unknown[]): number | undefined => {
  let total = 0
  let saw = false
  for (const item of items) {
    if (item == null || typeof item !== 'object' || !('ownedCount' in item)) {
      continue
    }
    const count = (item as { ownedCount?: unknown }).ownedCount
    if (typeof count !== 'number') continue
    total += count
    saw = true
  }
  return saw ? total : undefined
}

const shouldAttachTrace = (pathname: string, method: string): boolean => {
  if (method !== 'GET') return true
  return (
    pathname === '/user/auto_charge_suggestion' ||
    pathname === '/static/gift' ||
    pathname === '/static/sale_gift' ||
    pathname === '/user/gifted_users'
  )
}

const apiErrorSummary = (
  body: string
): {
  errorCode: string
  errorType: string
  message: string
} => {
  const trimmed = body.trim()
  if (!trimmed.startsWith('{')) {
    return {
      errorCode: '',
      errorType: '',
      message: '',
    }
  }
  const parsed = parseErrorResponseText({ body })
  return {
    errorCode: String(parsed.code),
    errorType: parsed.errorType,
    message: summarizeGiftMessage(parsed.message),
  }
}

const isAuthFailure = (status: number, errorType: string): boolean => {
  return status === 401 || errorType === 'token' || errorType === 'soft_banned'
}

const shouldReportApiIssue = (status: number): boolean => {
  return status >= 500 || status === 0
}

export const recordGiftApiSuccess = (args: {
  url: string
  method?: string
  data: unknown
}) => {
  try {
    if (!isClient()) return
    const endpoint = endpointFromUrl(args.url)
    if (endpoint == null || !GIFT_ENDPOINTS.has(endpoint.pathname)) return
    const method = (args.method ?? 'GET').toUpperCase()
    const trace = shouldAttachTrace(endpoint.pathname, method)
      ? freshTrace()
      : undefined
    giftBreadcrumb('api_ok', {
      endpoint: endpoint.pathname,
      method,
      http_status: 200,
      ...queryContext(endpoint.query, false),
      ...responseContext(args.data),
      ...traceFields(trace),
    })
  } catch (error) {
    console.error(error)
  }
}

export const recordGiftApiFailure = (args: {
  url?: string
  method?: string
  status?: number
  body?: string
  error?: unknown
}) => {
  try {
    if (!isClient()) return
    if (args.url == null) return
    const endpoint = endpointFromUrl(args.url)
    if (endpoint == null || !GIFT_ENDPOINTS.has(endpoint.pathname)) return
    const method = (args.method ?? 'GET').toUpperCase()
    const status = args.status ?? 0
    const summary = apiErrorSummary(args.body ?? '')
    const trace = shouldAttachTrace(endpoint.pathname, method)
      ? freshTrace()
      : undefined
    const owned = isAlreadyOwnedMessage(summary.message)
    const thrownMessage =
      status === 0 && args.error instanceof Error
        ? summarizeGiftMessage(args.error.message)
        : ''
    const context: Record<string, GiftValue | undefined> = {
      endpoint: endpoint.pathname,
      method,
      http_status: status,
      error_code: summary.errorCode || null,
      error_type: summary.errorType || null,
      message: summary.message || thrownMessage || null,
      gift_error: owned ? 'already_owned' : null,
      ...queryContext(endpoint.query, true),
      ...traceFields(trace),
    }
    giftBreadcrumb(
      isAuthFailure(status, summary.errorType) ? 'api_auth_error' : 'api_error',
      context
    )
    if (!shouldReportApiIssue(status)) return

    const fingerprint = [
      FLOW,
      'api',
      endpoint.pathname,
      String(status),
      summary.errorCode || summary.errorType || 'unknown',
    ]
    captureGiftIssue({
      message: `Gift API error ${method} ${endpoint.pathname} ${status}`,
      fingerprint,
      tags: {
        step: 'api',
        endpoint: endpoint.pathname,
        http_status: String(status),
        gift_id: trace?.gift_id ?? context.gift_id?.toString(),
        product_id: trace?.product_id,
        gift_error: owned ? 'already_owned' : undefined,
        target: trace?.target,
      },
      context,
      error:
        status === 0 && args.error instanceof Error ? args.error : undefined,
    })
  } catch (error) {
    console.error(error)
  }
}

const traceFields = (trace: GiftTrace | undefined): GiftData => {
  if (trace == null) return {}
  return compact({
    gift_id: trace.gift_id,
    product_id: trace.product_id,
    target: trace.target,
    chat_space_id: trace.chat_space_id,
    tweet_id: trace.tweet_id,
    tweet_reply_id: trace.tweet_reply_id,
    to_user_id: trace.to_user_id,
    payment_type: trace.payment_type,
  })
}

const bridgeExtra = (extra: unknown[]): GiftData => {
  const out: Record<string, GiftValue | undefined> = {}
  extra.forEach((item, index) => {
    if (typeof item === 'string') {
      out[`arg${index}`] = summarizeGiftMessage(item)
      return
    }
    if (item == null || typeof item === 'number' || typeof item === 'boolean') {
      out[`arg${index}`] = item
      return
    }
    if (typeof item !== 'object') return
    const record = item as Record<string, unknown>
    for (const key of [
      'status',
      'code',
      'error',
      'errorCode',
      'error_code',
      'message',
      'productId',
      'product_id',
      'giftId',
      'gift_id',
    ]) {
      const value = record[key]
      if (typeof value === 'number' || typeof value === 'boolean') {
        out[key] = value
      } else if (typeof value === 'string') {
        out[key] = summarizeGiftMessage(value)
      }
    }
  })
  return compact(out)
}

export const describeSaleGiftBridge = (
  handler: string,
  args: unknown[]
): BridgeRequest | undefined => {
  try {
    return describeSaleGiftBridgeUnsafe(handler, args)
  } catch (error) {
    console.error(error)
    return undefined
  }
}

const describeSaleGiftBridgeUnsafe = (
  handler: string,
  args: unknown[]
): BridgeRequest | undefined => {
  switch (handler) {
    case 'delegateSaleGiftsForChatSpace':
      return {
        handler,
        target: 'chat',
        chat_space_id: asId(args[0]),
        to_user_id: asId(args[1]),
      }
    case 'delegateSaleGiftsForTweet':
      return {
        handler,
        target: 'tweet',
        tweet_id: asId(args[0]),
      }
    case 'delegateSaleGiftsForReply':
      return {
        handler,
        target: 'reply',
        tweet_reply_id: asId(args[0]),
      }
    default:
      return undefined
  }
}

export const logBridgePurchaseRequest = (request: BridgeRequest) => {
  try {
    rememberBridgeRequest(request)
    giftBreadcrumb('bridge_purchase_request', request)
  } catch (error) {
    console.error(error)
  }
}

export const logBridgePurchaseWaiting = (request: BridgeRequest) => {
  try {
    rememberBridgeRequest(request)
    giftBreadcrumb('bridge_purchase_waiting', {
      ...request,
      dispatched: false,
    })
  } catch (error) {
    console.error(error)
  }
}

const detailString = (details: GiftData, key: string): string | undefined => {
  const value = details[key]
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

export const logBridgePurchaseResult = (result: string, extra: unknown[]) => {
  try {
    const request = freshBridgeRequest()
    const details = bridgeExtra(extra)
    const owned =
      isAlreadyOwnedMessage(result) ||
      isAlreadyOwnedMessage(Object.values(details).join(' '))
    const productId =
      detailString(details, 'product_id') ?? detailString(details, 'productId')
    const giftId =
      detailString(details, 'gift_id') ?? detailString(details, 'giftId')
    if (productId || giftId) {
      noteGiftTrace({
        product_id: productId,
        gift_id: giftId,
        target: request?.target,
      })
    }

    const status = summarizeGiftMessage(result).slice(0, 40)
    const code =
      detailString(details, 'error_code') ??
      detailString(details, 'errorCode') ??
      detailString(details, 'code') ??
      (typeof details.code === 'number' ? String(details.code) : '')
    // 購入結果は業務上の成否なので breadcrumb だけ残す。
    giftBreadcrumb('bridge_purchase_result', {
      status,
      handler: request?.handler,
      target: request?.target,
      chat_space_id: request?.chat_space_id,
      tweet_id: request?.tweet_id,
      tweet_reply_id: request?.tweet_reply_id,
      to_user_id: request?.to_user_id,
      error_code: code || null,
      gift_error: owned ? 'already_owned' : null,
      ...details,
    })
  } catch (error) {
    console.error(error)
  }
}

export const logBridgeHandlerError = (
  request: BridgeRequest,
  error: unknown
) => {
  try {
    logBridgeHandlerErrorUnsafe(request, error)
  } catch (loggingError) {
    console.error(loggingError)
  }
}

const logBridgeHandlerErrorUnsafe = (
  request: BridgeRequest,
  error: unknown
) => {
  const message =
    error instanceof Error
      ? summarizeGiftMessage(error.message)
      : typeof error === 'string'
      ? summarizeGiftMessage(error)
      : 'Gift purchase bridge call failed'
  const owned = isAlreadyOwnedMessage(message)
  captureGiftIssue({
    message: owned
      ? 'Gift purchase rejected as already owned'
      : 'Gift purchase bridge call failed',
    fingerprint: [
      FLOW,
      'bridge_purchase_request',
      request.handler,
      owned ? 'already_owned' : 'call_failed',
    ],
    tags: {
      step: 'bridge_purchase_request',
      gift_error: owned ? 'already_owned' : undefined,
      target: request.target,
    },
    context: {
      handler: request.handler,
      target: request.target,
      chat_space_id: request.chat_space_id ?? null,
      tweet_id: request.tweet_id ?? null,
      tweet_reply_id: request.tweet_reply_id ?? null,
      to_user_id: request.to_user_id ?? null,
      message,
    },
    error,
  })
}

export const logNativeInit = (args: {
  iapGiftEnabled: boolean
  faqEnabled: boolean
  redirectPath: string
  userId: string
}) => {
  try {
    setSentryUser(args.userId)
    const path = args.redirectPath.split('?')[0] ?? ''
    giftBreadcrumb('native_init', {
      iap_gift_enabled: args.iapGiftEnabled,
      faq_enabled: args.faqEnabled,
      redirect_path: path.startsWith('/') ? path.slice(0, 120) : null,
    })
  } catch (error) {
    console.error(error)
  }
}

const fingerprintKey = (fingerprint: string[] | undefined): string => {
  return (fingerprint ?? [FLOW]).join('|')
}

const giftIssueFingerprint = (event: Sentry.Event): string[] | undefined => {
  const marker = event.extra?.[GIFT_ISSUE_EXTRA]
  if (marker == null || typeof marker !== 'object' || Array.isArray(marker)) {
    return undefined
  }
  const fingerprint = (marker as { fingerprint?: unknown }).fingerprint
  if (!Array.isArray(fingerprint)) return undefined
  const parts: string[] = []
  for (let i = 0; i < fingerprint.length; i += 1) {
    const part = fingerprint[i]
    if (typeof part !== 'string') return undefined
    parts.push(part)
  }
  return parts
}

export const applyGiftSentryEvent: BeforeSend = (event) => {
  try {
    const fingerprint = giftIssueFingerprint(event)
    if (fingerprint == null) return event
    event.fingerprint = fingerprint
    if (!remember(recentEvents, fingerprintKey(fingerprint), EVENT_DEDUPE_MS)) {
      return null
    }
    return event
  } catch (error) {
    console.error(error)
    return event
  }
}
